import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const initHomeMotion = () => {
  const home = document.querySelector(".bt-mockup-home");
  const blog = document.querySelector(".bt-blog-page");
  const mobileScrollContainer = window.matchMedia("(max-width: 600px)").matches
    ? document.body
    : null;
  const withScrollContainer = (config) =>
    mobileScrollContainer
      ? { ...config, scroller: mobileScrollContainer }
      : config;
  const mobileRevealObserver =
    mobileScrollContainer && window.IntersectionObserver
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              gsap.to(entry.target, {
                autoAlpha: 1,
                y: 0,
                duration: 0.9,
                delay: Number(entry.target.dataset.btRevealDelay || 0),
                ease: "power3.out",
              });
              observer.unobserve(entry.target);
            });
          },
          {
            root: mobileScrollContainer,
            rootMargin: "0px 0px -14% 0px",
            threshold: 0,
          },
        )
      : null;
  if (new URLSearchParams(window.location.search).has("qa")) {
    document.documentElement.classList.add("bt-qa-overlay");
    if (new URLSearchParams(window.location.search).has("qa-red")) {
      document.documentElement.classList.add("bt-qa-red");
    }
    if (new URLSearchParams(window.location.search).has("qa-contrast")) {
      document.documentElement.classList.add("bt-qa-contrast");
    }
    return;
  }
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (blog) return;

  const referenceImages = document.querySelectorAll(
    ".bt-reference-page img, .bt-package-summary img",
  );
  referenceImages.forEach((image) => {
    gsap.fromTo(
      image,
      { "--image-parallax-y": "-18px" },
      {
        "--image-parallax-y": "18px",
        ease: "none",
        scrollTrigger: withScrollContainer({
          trigger: image,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        }),
      },
    );
  });

  document
    .querySelectorAll(".bt-reference-hero, .bt-package-hero")
    .forEach((hero) => {
      gsap.fromTo(
        hero,
        { "--parallax-y": "-24px" },
        {
          "--parallax-y": "24px",
          ease: "none",
          scrollTrigger: withScrollContainer({
            trigger: hero,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          }),
        },
      );
    });

  if (!home) return;

  document.body.classList.add("bt-home-motion-ready");

  home.querySelectorAll(".bt-home-photo").forEach((image) => {
    gsap.fromTo(
      image,
      { yPercent: -4, scale: 1.06 },
      {
        yPercent: 4,
        scale: 1.1,
        ease: "none",
        scrollTrigger: withScrollContainer({
          trigger: image,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        }),
      },
    );
  });

  home
    .querySelectorAll(
      ".bt-collection-card__media, .bt-collection-prestige__media",
    )
    .forEach((media) => {
      gsap.fromTo(
        media,
        { "--parallax-y": "-18px" },
        {
          "--parallax-y": "18px",
          ease: "none",
          scrollTrigger: withScrollContainer({
            trigger: media,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          }),
        },
      );
    });

  home.querySelectorAll(".bt-journey-card__media").forEach((media) => {
    const image = media.querySelector(".bt-journey-card__image");
    if (!image) return;

    gsap.fromTo(
      image,
      { yPercent: -5, scale: 1.08 },
      {
        yPercent: 5,
        scale: 1.12,
        ease: "none",
        scrollTrigger: withScrollContainer({
          trigger: media,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        }),
      },
    );
  });

  const revealTargets = home.querySelectorAll(
    ".bt-mockup-hero__copy, .bt-image-break__copy, .bt-experiences__heading > *, .bt-experience-card, .bt-direct-booking__copy, .bt-reviews .bt-shell > *, .bt-map-grid > div, .bt-newsletter > *, " +
      ".bt-story-section__copy > *, .bt-story-section__standard > *, " +
      ".bt-collection-section__intro > *, " +
      ".bt-journeys-section__intro > *, .bt-journey-card > .bt-eyebrow, .bt-journey-card > h3, .bt-journey-card > p:last-child, " +
      ".bt-process-section__intro > *, .bt-process-grid > article, .bt-process-section__stories-intro > *, .bt-story-quote-grid > article, " +
      ".bt-faq-section__intro > *, .bt-faq-item__trigger, .bt-faq-item__answer > p, .bt-faq-cta > h2, .bt-faq-cta > p, .bt-faq-cta > .bt-client-button",
  );

  revealTargets.forEach((element, index) => {
    element.classList.add("bt-reveal");
    if (mobileRevealObserver) {
      gsap.set(element, { autoAlpha: 0, y: 28 });
      mobileRevealObserver.observe(element);
      return;
    }
    gsap.fromTo(
      element,
      { autoAlpha: 0, y: 28 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 0.9,
        delay: index % 3 === 0 ? 0.05 : 0,
        ease: "power3.out",
        scrollTrigger: withScrollContainer({
          trigger: element,
          start: "top 86%",
          once: true,
        }),
      },
    );
  });

  const collectionCards = home.querySelectorAll(
    ".bt-collection-grid .bt-collection-card, .bt-collection-prestige",
  );
  const collectionSection = home.querySelector(".bt-collection-section");
  if (collectionCards.length) {
    collectionCards.forEach((card) => card.classList.add("bt-reveal"));
    if (mobileRevealObserver) {
      collectionCards.forEach((card) => {
        gsap.set(card, { autoAlpha: 0, y: 70 });
        mobileRevealObserver.observe(card);
      });
      return;
    }
    gsap.fromTo(
      collectionCards,
      { autoAlpha: 0, y: 70 },
      {
        autoAlpha: 1,
        y: 0,
        duration: 1.1,
        stagger: 0.18,
        ease: "power3.out",
        scrollTrigger: withScrollContainer({
          trigger: collectionSection,
          start: "top 80%",
          once: true,
        }),
      },
    );
  }

  home.querySelectorAll(".bt-parallax").forEach((section) => {
    gsap.fromTo(
      section,
      { "--parallax-y": "-24px" },
      {
        "--parallax-y": "24px",
        ease: "none",
        scrollTrigger: withScrollContainer({
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        }),
      },
    );
  });

  home.querySelectorAll(".bt-experience-card img").forEach((image) => {
    gsap.fromTo(
      image,
      { yPercent: -5, scale: 1.06 },
      {
        yPercent: 5,
        scale: 1.1,
        ease: "none",
        scrollTrigger: withScrollContainer({
          trigger: image,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        }),
      },
    );
  });
};

const initMobileSliderIndicators = () => {
  const sliderDefinitions = [
    [".bt-collection-grid", '[data-slider-indicator="collection"]', "article"],
    [".bt-journeys-grid", '[data-slider-indicator="journeys"]', "article"],
    [".bt-story-quote-grid", '[data-slider-indicator="reviews"]', "article"],
    [
      ".bt-blog-detail__related-list",
      '[data-slider-indicator="related-blog"]',
      ".bt-blog-detail__related-card",
    ],
    [
      ".bt-journal-categories",
      '[data-slider-indicator="journal-categories"]',
      "a",
    ],
  ];

  sliderDefinitions.forEach(
    ([sliderSelector, indicatorSelector, itemSelector]) => {
      const slider = document.querySelector(sliderSelector);
      const indicator = document.querySelector(indicatorSelector);
      if (!slider || !indicator) return;

      const items = Array.from(slider.querySelectorAll(itemSelector));
      if (items.length < 2) return;

      indicator.replaceChildren(
        ...items.map((_, index) => {
          const line = document.createElement("button");
          line.type = "button";
          line.setAttribute("aria-label", `Go to slide ${index + 1}`);
          line.addEventListener("click", () => {
            const step = items[1].offsetLeft - items[0].offsetLeft;
            slider.scrollTo({ left: step * index, behavior: "smooth" });
          });
          return line;
        }),
      );

      const update = () => {
        const step = items[1].offsetLeft - items[0].offsetLeft;
        const activeIndex = step
          ? Math.max(
              0,
              Math.min(items.length - 1, Math.round(slider.scrollLeft / step)),
            )
          : 0;
        indicator.querySelectorAll("button").forEach((line, index) => {
          line.classList.toggle("is-active", index === activeIndex);
          line.setAttribute(
            "aria-current",
            index === activeIndex ? "true" : "false",
          );
        });
      };

      slider.addEventListener("scroll", update, { passive: true });
      window.addEventListener("resize", update);
      update();
    },
  );
};

document.addEventListener("DOMContentLoaded", initHomeMotion);
document.addEventListener("DOMContentLoaded", initMobileSliderIndicators);
